/**
 * RBAC Permission Matrix
 * Single source of truth for authorization
 */

export type Role = 'user' | 'admin';

export type Action = 
  | 'view' | 'create' | 'update' | 'delete'
  | 'manage' | 'download' | 'purchase';

export type Resource = 
  | 'templates' | 'orders' | 'profile'
  | 'businesses' | 'business_reviews'
  | 'users' | 'admin_panel' | 'system_settings'
  | 'ai_conversations' | 'wallet' | 'credits'
  | 'knowledge_base';

/**
 * Permission matrix: role -> action -> resource -> allowed
 */
const PERMISSION_MATRIX: Record<Role, Record<Action, Resource[]>> = {
  user: {
    view: ['templates', 'orders', 'profile', 'businesses', 'business_reviews', 'ai_conversations', 'wallet', 'credits'],
    create: ['orders', 'business_reviews', 'ai_conversations'],
    update: ['profile', 'ai_conversations'],
    delete: ['ai_conversations'],
    manage: ['profile', 'wallet'],
    download: ['orders'],
    purchase: ['templates', 'credits'],
  },
  admin: {
    view: ['templates', 'orders', 'profile', 'businesses', 'business_reviews', 'users', 'admin_panel', 'system_settings', 'ai_conversations', 'wallet', 'credits', 'knowledge_base'],
    create: ['templates', 'businesses', 'users', 'system_settings'],
    update: ['templates', 'businesses', 'orders', 'users', 'system_settings'],
    delete: ['templates', 'businesses', 'business_reviews'],
    manage: ['admin_panel', 'system_settings', 'users', 'templates', 'businesses', 'knowledge_base'],
    download: ['templates', 'orders'],
    purchase: ['templates', 'credits'],
  },
};

/**
 * Canonical authorization function
 * @param role User's role
 * @param action Action being performed
 * @param resource Resource being accessed
 * @returns true if authorized, false otherwise
 */
export function can(role: Role | null | undefined, action: Action, resource: Resource): boolean {
  if (!role) return false;
  
  const allowedResources = PERMISSION_MATRIX[role]?.[action];
  if (!allowedResources) return false;
  
  return allowedResources.includes(resource);
}

/**
 * Check if user has admin role
 */
export function isAdmin(role: Role | null | undefined): boolean {
  return role === 'admin';
}

/**
 * Convert legacy roles to canonical role
 */
export function normalizeRole(roles: string[] | undefined | null): Role {
  if (!roles || !Array.isArray(roles)) return 'user';
  if (roles.includes('admin') || roles.includes('super_admin')) {
    return 'admin';
  }
  return 'user';
}
